import React from "react";
import { motion } from "framer-motion";
import { LayoutGrid, GanttChartSquare, Table2 } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { statusMeta, statusColor } from "./projectVisuals";

export const PROJECT_VIEWS = [
  { key: "grid", label: "Grid", icon: <LayoutGrid size={13} /> },
  { key: "timeline", label: "Timeline", icon: <GanttChartSquare size={13} /> },
  { key: "table", label: "Table", icon: <Table2 size={13} /> },
];

/**
 * Status filter chips — the page's primary way of narrowing the list.
 *
 * These replace the old four-count strip. Same numbers, but each one now does
 * something: the strip could only be read, a chip filters. That matters more
 * than usual here because the list is paginated server-side with a fixed
 * `createdAt` order — you can't sort finished projects out of the way, so
 * filtering them out is the move.
 *
 * A status with no projects is not rendered. An empty "Cancelled 0" chip is
 * furniture that costs a scan and answers nothing.
 */
export function StatusChips({ counts, value, onChange, order }) {
  const t = useDashTheme();
  const keys = ["all", ...(order || ["active", "planning", "on_hold", "completed", "cancelled", "archived"])];

  return (
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: t.s[5] }}>
      {keys.filter((k) => k === "all" || counts[k] > 0).map((k) => {
        const active = value === k;
        const label = k === "all" ? "All projects" : statusMeta(k).label;
        const dot = k === "all" ? null : statusColor(t, k);
        return (
          <button
            key={k}
            onClick={() => onChange(k)}
            aria-pressed={active}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, height: 30, padding: "0 12px",
              borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
              fontFamily: t.font.family, fontSize: t.font.size.md, fontWeight: 500,
              background: active ? t.c.text : t.c.surface,
              border: `1px solid ${active ? t.c.text : t.c.border}`,
              color: active ? t.c.textInverse : t.c.textSecondary,
              transition: "background 140ms, border-color 140ms, color 140ms",
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = t.c.surfaceHover; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = t.c.surface; }}
          >
            {dot && <span style={{ width: 7, height: 7, borderRadius: 99, background: dot, flexShrink: 0 }} />}
            {label}
            <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.75 }}>{counts[k] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Grid / Timeline / Table. Three renderings of the same page of records. */
export function ViewSwitch({ value, onChange }) {
  const t = useDashTheme();
  return (
    <div role="tablist" aria-label="Project view"
      style={{ display: "inline-flex", background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, borderRadius: 7, padding: 2 }}>
      {PROJECT_VIEWS.map((v) => {
        const active = value === v.key;
        return (
          <button
            key={v.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v.key)}
            style={{
              position: "relative", display: "inline-flex", alignItems: "center", gap: 6,
              height: 26, padding: "0 11px", border: "none", background: "transparent", cursor: "pointer",
              fontFamily: t.font.family, fontSize: t.font.size.md, fontWeight: 500,
              color: active ? t.c.text : t.c.textSecondary, transition: "color 140ms", borderRadius: 5,
            }}
          >
            {active && (
              <motion.span
                layoutId="projectViewPill"
                transition={t.motion.spring.snappy}
                style={{ position: "absolute", inset: 0, borderRadius: 5, background: t.c.surface, boxShadow: t.shadow.sm }}
              />
            )}
            <span style={{ position: "relative", display: "inline-flex" }}>{v.icon}</span>
            <span style={{ position: "relative" }}>{v.label}</span>
          </button>
        );
      })}
    </div>
  );
}
